import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { api, authStore, formatApiErrorDetail, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";

/**
 * Phase 12 — landing page for the Emergent-managed Google sign-in round-trip.
 *
 * Emergent sends the user back to:
 *   /auth/google/callback?redirect_to=/jarvis&chat_session_id=<id>#session_id=<token>
 *
 * We synchronously read the fragment during render (useLocation().hash, which
 * IS reactive — plain window.location.hash is not), POST the session_id to
 * our backend exchange endpoint, then navigate to redirect_to.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function AuthGoogleCallback() {
    const { setSession } = useAuth();
    const location = useLocation();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState("verifying"); // verifying | ok | error
    const [errorMsg, setErrorMsg] = useState("");
    // useRef (not useState) for the processed flag per Emergent playbook —
    // prevents StrictMode double-mount from running the exchange twice.
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = location.hash || "";
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            setStatus("error");
            setErrorMsg("We didn't receive a sign-in token from Google.");
            return;
        }
        const sessionId = decodeURIComponent(match[1]);
        const redirectTo = params.get("redirect_to") || "/jarvis";
        const chatSessionId = params.get("chat_session_id") || null;

        (async () => {
            try {
                const res = await api.post("/auth/google/session", {
                    session_id: sessionId,
                    chat_session_id: chatSessionId || undefined,
                    redirect_to: redirectTo,
                });
                const { session_token, user, redirect_to } = res.data || {};
                if (!session_token || !user) {
                    throw new Error("Empty session payload.");
                }
                authStore.set(session_token);
                setSession(session_token, user);
                setStatus("ok");
                // Strip the hash so a refresh doesn't retry.
                try { window.history.replaceState({}, "", window.location.pathname); } catch { /* noop */ }
                setTimeout(() => navigate(redirect_to || "/jarvis", { replace: true }), 600);
            } catch (err) {
                setErrorMsg(
                    formatApiErrorDetail(err?.response?.data?.detail) ||
                        "We couldn't finish your Google sign-in. Please try again.",
                );
                setStatus("error");
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div data-testid="auth-google-callback-page">
            <PageHeader
                eyebrow="Sign in"
                title="Finishing your Google sign-in…"
                subtitle="One moment while we attach your account."
            />
            <section className="mm-section bg-white">
                <div className="mm-container max-w-md">
                    <div className="mm-card p-8 text-center" data-testid="auth-google-callback-card">
                        {status === "verifying" && (
                            <>
                                <Loader2 className="mx-auto animate-spin text-mm-navy" size={28} />
                                <p className="mt-4 text-mm-muted">Verifying with Google…</p>
                            </>
                        )}
                        {status === "ok" && (
                            <>
                                <CheckCircle2 className="mx-auto text-emerald-600" size={28} />
                                <p className="mt-4 text-mm-ink">You're signed in. Redirecting…</p>
                            </>
                        )}
                        {status === "error" && (
                            <>
                                <AlertTriangle className="mx-auto text-amber-600" size={28} />
                                <p className="mt-4 text-mm-ink">{errorMsg}</p>
                                <button
                                    onClick={() => navigate("/login")}
                                    className="mm-btn-outline mt-6"
                                    data-testid="auth-google-callback-back"
                                >
                                    Back to sign-in
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
